import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { USERNAME_REGEXP } from "../../../../../config"
import { Role } from "../../../../../types"
import { MaintenanceOrder } from "../../../../../types/enum/MaintenanceOrder"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import {
  completeBilibiliVerification,
  deleteAccount,
  fetchProfile,
  sendMaintenanceOrder,
  startBilibiliVerification,
  unlinkBilibiliVerification
} from "../../../network"
import { changeName, setErrorAlertMessage } from "../../../stores/NetworkStore"

export function AccountTab() {
  const { t } = useTranslation()
  const user = useAppSelector((state) => state.network.profile)

  const promptDeleteAccount = () => {
    const confirmation = prompt(t("profile.account.delete_account_confirmation"))
    if (confirmation === t("profile.account.delete_account_passphrase")) {
      deleteAccount()
    } else if (confirmation != null) {
      alert(t("profile.account.delete_account_confirmation_failed"))
    }
  }

  return user ? (
    <div>
      <ChangeNameForm />
      <BilibiliLinkSection />
      <h3>{t("profile.account.user_id")}</h3>
      <p>
        {t("profile.account.user_id_hint1")}{" "}
        <span style={{ color: "red" }}>{user.uid}</span>
      </p>
      <p>{t("profile.account.user_id_hint2")}</p>
      <h3>{t("profile.account.delete_account")}</h3>
      <p>{t("profile.account.delete_account_hint")}</p>
      <button className="bubbly red" onClick={() => promptDeleteAccount()}>
        {t("profile.account.delete_account")}
      </button>
      {user.role === Role.ADMIN && (
        <>
          <h3>{t("admin_panel.heap_snapshot")}</h3>
          <button
            className="bubbly red"
            onClick={() => sendMaintenanceOrder(MaintenanceOrder.HEAP_SNAPSHOT)}
          >
            {t("admin_panel.heap_snapshot")}
          </button>
        </>
      )}
    </div>
  ) : null
}

function BilibiliLinkSection() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.network.profile)
  const [midInput, setMidInput] = useState<string>("")
  const [loadingAction, setLoadingAction] = useState<
    "start" | "verify" | "unlink" | null
  >(null)
  const [pendingCode, setPendingCode] = useState<string | null>(
    user?.bilibiliVerificationCode ?? null
  )
  const [pendingMid, setPendingMid] = useState<string | null>(
    user?.bilibiliVerificationMid ?? null
  )

  if (!user) return null

  function reportError(error: unknown, fallback: string) {
    dispatch(
      setErrorAlertMessage(error instanceof Error ? error.message : fallback)
    )
  }

  async function handleStart() {
    if (!midInput.trim()) return
    setLoadingAction("start")
    try {
      const payload = await startBilibiliVerification(midInput.trim())
      setPendingCode(payload.code)
      setPendingMid(midInput.trim())
    } catch (error) {
      reportError(error, "Unable to start Bilibili verification")
    } finally {
      setLoadingAction(null)
    }
  }

  async function handleVerify() {
    setLoadingAction("verify")
    try {
      await completeBilibiliVerification()
      setPendingCode(null)
      setPendingMid(null)
      await fetchProfile(true)
    } catch (error) {
      reportError(error, "Unable to verify Bilibili account")
    } finally {
      setLoadingAction(null)
    }
  }

  async function handleUnlink() {
    const confirmed = window.confirm(
      t("profile.account.bilibili_unlink_confirmation")
    )
    if (!confirmed) return

    setLoadingAction("unlink")
    try {
      await unlinkBilibiliVerification()
      setPendingCode(null)
      setPendingMid(null)
      await fetchProfile(true)
    } catch (error) {
      reportError(error, "Unable to unlink Bilibili account")
    } finally {
      setLoadingAction(null)
    }
  }

  if (user.bilibiliMid && user.bilibiliVerifiedAt) {
    return (
      <div>
        <h3>{t("profile.account.bilibili_title")}</h3>
        <p>
          {t("profile.account.bilibili_linked_as", {
            name: user.bilibiliName
          })}
        </p>
        <p>
          <a
            href={`https://space.bilibili.com/${user.bilibiliMid}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("profile.account.bilibili_open_space")}
          </a>
        </p>
        <button
          className="bubbly red"
          disabled={loadingAction != null}
          onClick={() => handleUnlink()}
        >
          {loadingAction === "unlink"
            ? t("profile.account.bilibili_unlinking")
            : t("profile.account.bilibili_unlink")}
        </button>
      </div>
    )
  }

  if (pendingCode && pendingMid) {
    return (
      <div>
        <h3>{t("profile.account.bilibili_title")}</h3>
        <p>{t("profile.account.bilibili_pending_hint1", { mid: pendingMid })}</p>
        <p>
          {t("profile.account.bilibili_pending_hint2")}{" "}
          <span style={{ color: "red" }}>{pendingCode}</span>
        </p>
        <p>{t("profile.account.bilibili_pending_hint3")}</p>
        <div style={{ display: "flex", gap: "0.5em" }}>
          <button
            className="bubbly blue"
            disabled={loadingAction != null}
            onClick={() => handleVerify()}
          >
            {loadingAction === "verify"
              ? t("profile.account.bilibili_verifying")
              : t("profile.account.bilibili_verify")}
          </button>
          <button
            className="bubbly red"
            disabled={loadingAction != null}
            onClick={() => {
              setPendingCode(null)
              setPendingMid(null)
            }}
          >
            {t("profile.account.bilibili_cancel")}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h3>{t("profile.account.bilibili_title")}</h3>
      <p>{t("profile.account.bilibili_hint")}</p>
      <div style={{ display: "flex", gap: "0.5em" }}>
        <input
          type="text"
          placeholder={t("profile.account.bilibili_uid_placeholder")}
          value={midInput}
          onChange={(e) => setMidInput(e.target.value)}
        />
        <button
          className="bubbly blue"
          disabled={loadingAction != null || !midInput.trim()}
          onClick={() => handleStart()}
        >
          {loadingAction === "start"
            ? t("profile.account.bilibili_generating_code")
            : t("profile.account.bilibili_link")}
        </button>
      </div>
    </div>
  )
}

function ChangeNameForm() {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState<string>("")
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.network.profile)
  const isAnonymous = false // TODO: retrieve from profile if we decide to add back anonymous users

  function tryChangeName(newName: string) {
    // Remove all invisible characters
    newName = newName.replace(/[\u3164\u200B-\u200D\u2060\uFEFF]/g, "")
    if (USERNAME_REGEXP.test(newName)) {
      dispatch(changeName(newName))
    } else {
      dispatch(setErrorAlertMessage(t("profile.account.invalid_username")))
    }
  }

  if (user && isAnonymous) {
    return (
      <div className="my-container">
        <p>{t("profile.account.anonymous_users_name_hint")}</p>
      </div>
    )
  }

  return user ? (
    <div>
      <h3>{t("profile.account.change_name")}</h3>
      <div style={{ display: "flex", gap: "0.5em" }}>
        <input
          type="text"
          placeholder={user.displayName}
          onChange={(e) => {
            setInputValue(e.target.value)
          }}
        />
        <button
          className="bubbly blue"
          onClick={() => tryChangeName(inputValue)}
        >
          {t("change")}
        </button>
      </div>
      <p className="disclaimer">{t("profile.account.username_disclaimer")}</p>
    </div>
  ) : null
}
